import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { sanitizeImageUrl } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

export default function Leaderboard({ t, data, searchTerm, slug, isLoading }) {
  const [sortColumn, setSortColumn] = useState('points');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(col);
      setSortAsc(false);
    }
  };

  const rankedData = useMemo(() => {
    const byPoints = [...data].sort((a, b) => (b.points || 0) - (a.points || 0));
    byPoints.forEach((p, i) => { p.pointRank = i + 1; });
    return byPoints;
  }, [data]);

  const sortedData = useMemo(() => {
    let filtered = rankedData;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = rankedData.filter(p => (p.name || '').toLowerCase().includes(term));
    }
    return [...filtered].sort((a, b) => {
      let aVal = a[sortColumn] ?? -Infinity;
      let bVal = b[sortColumn] ?? -Infinity;
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [rankedData, searchTerm, sortColumn, sortAsc]);

  const SortHeader = ({ col, children, className = '', title }) => {
    const isActive = sortColumn === col;
    const indicator = isActive ? (sortAsc ? '⇧' : '⇩') : '⇅';
    return (
      <th
        className={`p-2 text-center cursor-pointer hover:bg-gray-600 transition ${className}`}
        onClick={() => handleSort(col)}
        title={title}
      >
        {children} <span className="sort-indicator" style={{ color: isActive ? '#fbbf24' : '' }}>{indicator}</span>
      </th>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 100 }}>
          <LoadingSpinner />
        </div>
      )}
      <div className="leaderboard-scroll-wrapper">
      <table className="leaderboard-table bg-gray-800 rounded-xl text-xs md:text-sm whitespace-nowrap">
        <thead>
          <tr className="bg-gradient-to-r from-blue-900 to-blue-800 text-white text-xs font-bold border-b-2 border-blue-600">
            <th colSpan="3" className="p-2 md:p-3 text-center border-r border-blue-600 lb-sticky-player lb-sticky-header-group">👤 <span>{t('col_player')}</span></th>
            <th colSpan="4" className="p-2 md:p-3 text-center border-r border-blue-600">🗡️ <span>{t('col_attack_quality')}</span></th>
            <th colSpan="4" className="p-2 md:p-3 text-center border-r border-blue-600">🛡️ <span>{t('col_defense_quality')}</span></th>
            <th colSpan="6" className="p-2 md:p-3 text-center">📊 <span>{t('col_points')}</span></th>
          </tr>
          <tr className="bg-gray-700 border-b-2 border-gray-600">
            <th className="p-2 text-center text-gray-300 lb-sticky-rank">#</th>
            <SortHeader col="name" className="text-left text-gray-300 lb-sticky-name">{t('col_player_name')}</SortHeader>
            <SortHeader col="th" className="text-center text-gray-300">{t('col_townhall')}</SortHeader>
            <SortHeader col="total_attacks" className="text-gray-300" title={t('tooltip_total_attacks')}>#⚔️</SortHeader>
            <SortHeader col="three_star_pct" className="text-green-400" title={t('tooltip_3star')}>3⭐%</SortHeader>
            <SortHeader col="two_star_pct" className="text-yellow-400" title={t('tooltip_2star')}>2⭐%</SortHeader>
            <SortHeader col="failed_pct" className="text-yellow-600" title={t('tooltip_failed')}>❌</SortHeader>
            <SortHeader col="total_defenses" className="text-gray-300" title={t('tooltip_total_defenses')}>#🛡️</SortHeader>
            <SortHeader col="defense_excellent_pct" className="text-green-400" title={t('tooltip_defense_excellent')}>✓✓⭐%</SortHeader>
            <SortHeader col="defense_okay_pct" className="text-yellow-400" title={t('tooltip_defense_okay')}>2⭐%</SortHeader>
            <SortHeader col="defense_poor_pct" className="text-red-400" title={t('tooltip_defense_poor')}>3⭐%</SortHeader>
            <SortHeader col="cw_points" className="text-amber-400" title={t('tooltip_cw_points')}>⚔️</SortHeader>
            <SortHeader col="cwl_points" className="text-amber-400" title={t('tooltip_cwl_points')}>🏆</SortHeader>
            <SortHeader col="defense_points" className="text-amber-400" title={t('tooltip_defense_points')}>🛡️</SortHeader>
            <SortHeader col="missed_attack_points" className="text-amber-400" title={t('tooltip_missed_attacks')}>🚫</SortHeader>
            <SortHeader col="extra_points" className="text-amber-400" title={t('tooltip_extra_points')}>➕</SortHeader>
            <th
              className="p-2 md:p-3 text-right text-white font-bold bg-gradient-to-l from-blue-700 to-blue-800 cursor-pointer hover:from-blue-600 hover:to-blue-700 transition"
              onClick={() => handleSort('points')}
            >
              <span>{t('col_total')}</span> <span className="sort-indicator" style={{ color: sortColumn === 'points' ? '#fbbf24' : '' }}>{sortColumn === 'points' ? (sortAsc ? '⇧' : '⇩') : '⇅'}</span>
            </th>
          </tr>
        </thead>
        <tbody id="leaderboard">
          {sortedData.map((player, i) => (
            <PlayerRow key={player.name + i} player={player} rank={i + 1} t={t} slug={slug} />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  const toggle = useCallback(() => setShow(prev => !prev), []);
  return (
    <span className="info-tooltip-wrapper" onClick={toggle} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span className="cursor-help">ℹ️</span>
      {show && <span className="info-tooltip-popup">{text}</span>}
    </span>
  );
}

function PlayerRow({ player, rank, t, slug }) {
  const playerTag = player.tag ? player.tag.replace('#', '') : '';

  return (
    <tr className="border-t border-gray-600 hover:bg-gray-700 transition">
      <td className="p-2 text-center lb-sticky-rank">{player.pointRank || rank}</td>
      <td className="px-2 py-1 lb-sticky-name">
        <Link 
          to={`/clan/${slug}/player/${playerTag}`}
          className="flex items-center gap-2 hover:text-blue-400 transition-colors"
        >
          <img src={sanitizeImageUrl(player.league_icon)} className="w-5 h-5 flex-shrink-0" alt="League" />
          <span className="truncate">{player.name}</span>
        </Link>
      </td>
      <td className="text-center px-2 py-1">
        <img src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${player.th}.webp`} className="w-7 h-7 mx-auto flex-shrink-0" alt={`TH${player.th}`} title={`${t('col_townhall')} ${player.th}`} />
      </td>
      <td className="text-center text-gray-300 px-2 py-1">
        {player.total_attacks > 0 
          ? `${(player.total_attacks - (player.missed_attacks || 0))}/${player.total_attacks}` 
          : '0/0'}
      </td>
      <td className="text-center text-green-400 px-2 py-1">{player.three_star_pct !== null ? `${player.three_star_pct}%` : <InfoTooltip text={t('tooltip_min_attacks')} />}</td>
      <td className="text-center text-yellow-400 px-2 py-1">{player.two_star_pct !== null ? `${player.two_star_pct}%` : <InfoTooltip text={t('tooltip_min_attacks')} />}</td>
      <td className="text-center text-yellow-600 px-2 py-1">{player.failed_pct !== null ? `${player.failed_pct}%` : <InfoTooltip text={t('tooltip_min_attacks')} />}</td>
      <td className="text-center text-gray-300 px-2 py-1">{player.total_defenses || 0}</td>
      <td className="text-center text-green-400 px-2 py-1">{player.defense_excellent_pct !== null ? `${player.defense_excellent_pct}%` : <InfoTooltip text={t('tooltip_min_defenses')} />}</td>
      <td className="text-center text-yellow-400 px-2 py-1">{player.defense_okay_pct !== null ? `${player.defense_okay_pct}%` : <InfoTooltip text={t('tooltip_min_defenses')} />}</td>
      <td className="text-center text-red-400 px-2 py-1">{player.defense_poor_pct !== null ? `${player.defense_poor_pct}%` : <InfoTooltip text={t('tooltip_min_defenses')} />}</td>
      <td className="text-center text-amber-400 px-2 py-1">{player.cw_points || 0}</td>
      <td className="text-center text-amber-400 px-2 py-1">{player.cwl_points || 0}</td>
      <td className="text-center text-amber-400 px-2 py-1">{player.defense_points || 0}</td>
      <td className="text-center text-amber-400 px-2 py-1">{player.missed_attack_points || 0}</td>
      <td className="text-center text-amber-400 px-2 py-1">{player.extra_points || 0}</td>
      <td className="text-right font-bold bg-gradient-to-l from-blue-700 to-blue-800 text-white px-3 py-2">{player.points}</td>
    </tr>
  );
}
